// main.js — entry point: wires the stage, cards, scroll and parallax together,
// plus the page-level interactions (site.js). The 3D helix experience is
// preserved intact — it now lives inside the pinned .helixArea section.
import * as THREE from 'three';
import { createStage } from './scene.js';
import { buildCards } from './cards.js';
import { buildHeadlines } from './headlines.js';
import { createParallax } from './parallax.js';
import { initScroll } from './scroll.js';
import { TOTAL_OFFSET } from './helix.js';
import { initSite } from './site.js';
import { initReel } from './reel.js';
import { initAboutJourney } from './about.js';
import { initFolio } from './folio.js';
import { initServices } from './services.js';
import { initDecoWaves } from './deco-waves.js';
import { initHelixRails } from './helix-rails.js';
import { initSmoothScroll } from './smooth-scroll.js';

// portfolio screenshots — 10 cards along the ribbon
const IMAGES = [
  './img/pic01.jpg',
  './img/pic02.jpg',
  './img/pic03.jpg',
  './img/pic04.png',
  './img/05.png',
  './img/06.png',
  './img/07.png',
  './img/08.png',
  './img/09.png',
  './img/10.png',
];

// start the ribbon well below the origin so at p=0 it sits below the screen;
// as offset grows it winds UP the spiral path into view and out the top.
// (chosen so the band is centred on screen at mid-scroll, p≈0.5)
const START_OFFSET = -11.5;

const isMobile = matchMedia('(max-width: 768px)').matches;
const reduced  = matchMedia('(prefers-reduced-motion: reduce)').matches;
const smoothScroll = initSmoothScroll({ reduced });
addEventListener('pagehide', () => smoothScroll.destroy(), { once: true });

const loaderEl = document.querySelector('[data-loader]');
const fillEl   = document.querySelector('[data-loader-fill]');
const pctEl    = document.querySelector('[data-loader-pct]');
function setProgress(p) {
  const v = Math.round(p * 100);
  if (fillEl) fillEl.style.width = v + '%';
  if (pctEl)  pctEl.textContent = v;
}
function hideLoader() {
  setProgress(1);
  loaderEl?.classList.add('is-done');
  document.body.classList.add('is-ready');   // triggers the hero line reveal
  smoothScroll.start();
  reel.restart();                            // slide one gets its full run now
}

// page-level interactions (cursor, reveals, nav, work previews …)
initSite();

// About: horizontal "film strip" — pin + scrub, parallax, clip reveals, HUD
initAboutJourney();

// Folio: black vertical "index reel" — pin + per-item fall-to-centre focus
initFolio();

// Services: kinetic capabilities — velocity marquee + interactive hover rows
initServices();

// Helix: the two decorative sweeps ripple like slack cables (CSS/SVG only)
initDecoWaves();

// Helix: flanking rails — scroll meter, running index and tick scale
initHelixRails();

// hero background impact reel (runs on every path — independent of the helix)
const reel = initReel();
reel.start();

// the live WebGL stage, kept so failures and unload can tear it down again
let stage = null;

// ── mobile: skip WebGL, render a CSS scroll gallery ──────────────────────
if (isMobile) {
  buildFallback();
  hideLoader();
} else {
  // hideLoader() is the LAST step of boot(), so any failure in between —
  // no WebGL support, a missing texture, a lost GPU context — would otherwise
  // leave the fixed full-screen loader covering the page forever.
  boot().catch(onBootFailure);
}

async function boot() {
  const section = document.querySelector('[data-helix-area]');
  const sticky  = document.querySelector('[data-helix-sticky]');
  stage = createStage(sticky);
  const { scene, camera, render } = stage;

  const manager = new THREE.LoadingManager();
  manager.onProgress = (_url, loaded, total) => setProgress(loaded / total);

  const cards = await buildCards(IMAGES, manager);

  // compose the group like the reference: uniform lean (keeps every seam intact)
  // Lower the complete spiral in the viewport while preserving its path/animation.
  const BASE_Y = -1.1;
  cards.group.position.y = BASE_Y;
  cards.group.rotation.x = -0.1;
  cards.group.rotation.z = 0.1;   // stronger diagonal tilt of the whole ribbon
  scene.add(cards.group);

  // title lives INSIDE the scene at the helix core so the ribbon weaves around it
  const headlines = buildHeadlines();
  scene.add(headlines.group);
  // hide the DOM headlines — the 3D ones replace them on the WebGL path
  document.querySelectorAll('.headline').forEach((el) => { el.style.display = 'none'; });

  const parallax = createParallax();

  // no rigid block translation: the ribbon enters and exits purely by travelling
  // ALONG the spiral path — offset winds it up from below-screen to above-screen.
  const draw = (offset) => {
    cards.update(offset);
    headlines.update((offset - START_OFFSET) / TOTAL_OFFSET);
    parallax.update(offset);
    render();
  };

  if (reduced) {
    // freeze mid-helix, no scroll drive, no parallax drift
    draw(START_OFFSET + TOTAL_OFFSET * 0.5);
  } else {
    window.__scroll = initScroll(draw, START_OFFSET, section);
  }

  // intro flourish — cards ease in from a tighter, deeper arc
  draw(START_OFFSET);
  hideLoader();

  // live-tuning handle (dev only)
  window.__stage = { camera, render, group: cards.group, draw, START_OFFSET };

  // dispose on unload to avoid GPU leaks
  addEventListener('pagehide', () => {
    cards.dispose();
    headlines.dispose();
    stage?.dispose();
  }, { once: true });
}

// WebGL path failed — tear down whatever was built, fall back to the CSS
// gallery and release the loader so the rest of the page stays usable.
function onBootFailure(err) {
  console.error('[helix] WebGL boot failed, falling back to the CSS gallery:', err);
  try { stage?.dispose(); } catch { /* context may already be gone */ }
  stage = null;
  // boot() may have hidden the DOM headlines before failing — restore them
  document.querySelectorAll('.headline').forEach((el) => { el.style.display = ''; });
  buildFallback();
  hideLoader();
}

function buildFallback() {
  document.body.classList.add('is-fallback');
  const fb   = document.querySelector('[data-fallback]');
  const rail = document.querySelector('[data-fallback-rail]');
  fb?.removeAttribute('hidden');
  IMAGES.forEach((src) => {
    const card = document.createElement('div');
    card.className = 'fallback__card';
    const img = document.createElement('img');
    img.src = src;
    img.loading = 'lazy';
    img.alt = 'Selected work';
    card.appendChild(img);
    rail?.appendChild(card);
  });
}
